import { emitRequestError } from './requestErrorEvents';

function normalizeSymbol(symbol) {
  if (symbol == null) return '';
  const normalized = String(symbol).trim().toUpperCase().replace(/^\.\//, '/');
  const venueSeparator = normalized.indexOf(':');
  return venueSeparator > 0 ? normalized.substring(0, venueSeparator) : normalized;
}

function futuresRootSymbol(symbol) {
  const normalized = normalizeSymbol(symbol);
  if (!normalized.startsWith('/')) return '';

  const root = normalized.replace(/^\/([A-Z]+)[FGHJKMNQUVXZ][0-9]{1,2}$/, '/$1');
  return root !== normalized ? root : '';
}

function marketDataSymbols(symbol) {
  const normalized = normalizeSymbol(symbol);
  const root = futuresRootSymbol(normalized);
  return [normalized, root].filter(Boolean).filter(
    (value, index, array) => array.indexOf(value) === index
  );
}

export function requiresBrokerage(dataSource) {
  return dataSource === 'LIVE';
}

export function getMarketDataRoute({
  dataSource,
  brokerage,
  messageType,
  symbol,
}) {
  if (!requiresBrokerage(dataSource)) {
    return {
      dataSource,
      brokerage,
      valid: true,
    };
  }

  if (brokerage && brokerage !== 'none') {
    return {
      dataSource,
      brokerage,
      valid: true,
    };
  }

  emitRequestError({
    symbol,
    notification:
      `Blocked ambiguous LIVE ${messageType} request. Select an account/brokerage first.`,
  });

  return {
    dataSource,
    brokerage,
    valid: false,
  };
}

/**
 * @param {{
 *   symbol?: string,
 *   dataSource?: string,
 *   brokerage?: string,
 *   from?: string
 * }} params
 */
export function buildMarketDataKey({ symbol, dataSource, brokerage, from }) {
  const route = from || (requiresBrokerage(dataSource) ? brokerage : dataSource);
  const [normalizedSymbol] = marketDataSymbols(symbol);
  if (!normalizedSymbol || !route) return '';
  return `${normalizedSymbol}-${route}`;
}

export function buildMarketDataKeys({ symbol, dataSource, brokerage }) {
  return marketDataSymbols(symbol)
    .flatMap((marketDataSymbol) => [
      buildMarketDataKey({ symbol: marketDataSymbol, dataSource, brokerage }),
      buildMarketDataKey({ symbol: marketDataSymbol, from: dataSource }),
      buildMarketDataKey({ symbol: marketDataSymbol, from: brokerage }),
    ])
    .filter(Boolean)
    .filter((value, index, array) => array.indexOf(value) === index);
}

function normalizeTimeUnit(timeUnit) {
  if (timeUnit == null) return '';
  return String(timeUnit).trim().toUpperCase();
}

function normalizeCandleDuration(candleDuration) {
  const normalizedDuration = Number(candleDuration);
  if (!Number.isFinite(normalizedDuration) || normalizedDuration <= 0) {
    return '';
  }
  return String(normalizedDuration);
}

export function buildMarketDataCandleKey({
  symbol,
  dataSource,
  brokerage,
  from,
  timeUnit,
  candleDuration,
}) {
  const routeKey = buildMarketDataKey({ symbol, dataSource, brokerage, from });
  return appendCandleQualifier(routeKey, { timeUnit, candleDuration });
}

function appendCandleQualifier(routeKey, { timeUnit, candleDuration }) {
  const normalizedTimeUnit = normalizeTimeUnit(timeUnit);
  const normalizedDuration = normalizeCandleDuration(candleDuration);

  if (!routeKey || !normalizedTimeUnit || !normalizedDuration) {
    return routeKey;
  }

  return `${routeKey}|${normalizedDuration}-${normalizedTimeUnit}`;
}

export function buildMarketDataCandleKeys({
  symbol,
  dataSource,
  brokerage,
  timeUnit,
  candleDuration,
  includeRouteFallback = false,
}) {
  const routeKeys = buildMarketDataKeys({ symbol, dataSource, brokerage });
  const candleKeys = routeKeys
    .map((routeKey) => appendCandleQualifier(routeKey, { timeUnit, candleDuration }))
    .filter(Boolean);

  const combinedKeys = includeRouteFallback
    ? [...candleKeys, ...routeKeys]
    : candleKeys;

  return combinedKeys.filter(
    (value, index, array) => array.indexOf(value) === index
  );
}

export function getSingleLiveBrokerage(balances = {}) {
  const distinct = getLiveBrokerages(balances);
  return distinct.length === 1 ? distinct[0] : '';
}

export function getLiveBrokerages(balances = {}) {
  const brokerages = Object.values(balances)
    .map((balance) => balance?.brokerage)
    .filter((brokerage) => brokerage && brokerage !== 'BACKTEST');

  return [...new Set(brokerages)];
}
